import React, { useState, useEffect } from "react";
import styles from "./styles.module.scss";
import SensorSticker from "../SensorSticker";
import SensorStatusTag from "../SensorStatusTag";
import InstallSensorLocation from "../InstallSensorLocation";

import classNames from "classnames";
import { getLastEventStatus } from "Services/Utils/newDeviceMonitoring/getLastEvent";
import {
  AugmentDeviceData,
  SensorStatus,
} from "Services/Utils/newDeviceMonitoring/types";
import { EditIcon } from "Styles/Icons";

interface SensorProps {
  device: AugmentDeviceData;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  sensorStatus: SensorStatus;
  isOffline: boolean;
  onSaveLocation?: (
    device: AugmentDeviceData,
    newValue: string
  ) => Promise<void>;
}

const Sensor = ({
  device,
  onClick,
  sensorStatus,
  isOffline,
  onSaveLocation,
}: SensorProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Display sensorLocationafter a successful save without waiting for Redux state changes
  const [displayLocation, setDisplayLocation] = useState<string>(
    device.sensorLocation ?? ""
  );

  // Keep in sync when the augmented device data updates (e.g. after Redux update)
  useEffect(() => {
    if (!isEditing) {
      setDisplayLocation(device.sensorLocation ?? "");
    }
  }, [device.sensorLocation, isEditing]);

  const { lastEvent } = getLastEventStatus(device);

  // Show the edit button for non-hub sensors that have location options
  const isHub = device.label?.toUpperCase() === "H1";
  const canEditLocation =
    !isHub &&
    device.locationRequired !== false &&
    !!device.locationOptions?.length &&
    !!onSaveLocation;

  const handleLocationChange = async (location: { value: string }) => {
    if (!onSaveLocation || isSaving) return;
    setSaveError(null);
    setIsSaving(true);
    try {
      await onSaveLocation(device, location.value);
      setDisplayLocation(location.value);
      setIsEditing(false);
    } catch {
      setSaveError("Could not save location. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={classNames(styles.sensor, {
        [styles.hover]: sensorStatus.variant === "offline" && !isEditing,
        [styles.editing]: isEditing,
      })}
      onClick={!isEditing ? onClick : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styles.sensor_left}>
        <div className={styles.sensor_name_container}>
          <p className={styles.sensor_name}>
            {device?.displayName || device?.defaultName}
          </p>
          {!isEditing && displayLocation && displayLocation !== "other" ? (
            <p className={styles.location}>{displayLocation}</p>
          ) : null}
          {canEditLocation && !isEditing && (
            <button
              className={styles.edit_button}
              onClick={(e) => {
                e.stopPropagation();
                setSaveError(null);
                setIsEditing(true);
              }}
              aria-label="Edit sensor location"
            >
              <EditIcon style={{ width: 14, height: 14 }} />
              <span>Edit sensor location</span>
            </button>
          )}
        </div>
        <div className={styles.sensor_signal}>
          <SensorStatusTag
            sensorStatus={sensorStatus}
            isHovered={isHovered && !isEditing}
          />
          {isOffline && lastEvent ? (
            <p
              className={classNames(
                styles.last_online,
                styles.mobile_last_online
              )}
            >
              Last online: {lastEvent}
            </p>
          ) : null}
        </div>
      </div>
      <div className={styles.sensor_right}>
        <div className={styles.device_group}>
          <img src={device?.img} className={styles.image} alt="sensor" />
          <SensorSticker label={device?.label} />
        </div>
        {isOffline && lastEvent ? (
          <div className={styles.desktop_last_online}>
            <p className={styles.last_online}>Last online: {lastEvent}</p>
          </div>
        ) : null}
      </div>

      {/* Edit location section */}
      {isEditing && (
        <div
          className={styles.edit_location_container}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.edit_location_header}>
            <p className={styles.edit_location_title}>Edit location</p>
            <button
              className={styles.cancel_button}
              onClick={() => {
                setIsEditing(false);
                setSaveError(null);
              }}
            >
              Cancel
            </button>
          </div>
          <InstallSensorLocation
            sensor={{
              name: device.location ?? device.child_device_internal_id,
              locationRequired: true,
              locationOptions: device.locationOptions ?? [],
              label: device.label?.toLowerCase(),
            }}
            isHub={false}
            showLocationError={false}
            onLocationChange={handleLocationChange}
            currentLocation={displayLocation}
          />
          {isSaving && (
            <p className={styles.saving_text}>Saving…</p>
          )}
          {saveError && (
            <p className={styles.save_error}>{saveError}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Sensor;
